import classNames from 'classnames';
import { useCallback, useContext } from 'react';
import { useBuildNotificationFromJson } from '@/modules/common/hooks/useBuildNotificationFromJson.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerReadNotificationModal } from '@/modules/issuer-dashboard/components/modals/IssuerReadNotificationModal.tsx';
import { useIssuerMakeIsReadNotificationsMutation } from '@/modules/issuer-dashboard/queries/event-logs.ts';
import { ModalProviderContext } from 'ui';
import { fixNameEnum } from 'ui/utils';
import { EventLogResponse } from 'api';

interface IssuerDashboardNotificationItemProps {
  notification: EventLogResponse;
}

const IssuerDashboardNotificationItem = ({
  notification,
}: IssuerDashboardNotificationItemProps) => {
  const { type, data, created_at, is_read } = notification;
  const text = useBuildNotificationFromJson(type, data, false);
  const { openModal } = useContext(ModalProviderContext);
  const getRegisterTypeString = useRegisterTypeStrings();
  const { mutateAsync, isLoading } = useIssuerMakeIsReadNotificationsMutation();

  const handleClickNotification = useCallback(() => {
    if (!notification.is_read) {
      mutateAsync({ ids: [notification.id] });
    }
    openModal(IssuerReadNotificationModal, {
      type: notification.type,
      data: notification.data,
      created_at: dateToPrint(notification?.created_at) || '-',
    });
  }, [
    mutateAsync,
    notification?.created_at,
    notification.data,
    notification.id,
    notification.is_read,
    notification.type,
    openModal,
  ]);

  return (
    <div
      className={classNames('flex gap-3', { 'cursor-pointer hover:bg-gray-100': !isLoading })}
      onClick={isLoading ? undefined : handleClickNotification}
    >
      <div
        className={classNames('w-2 h-2 rounded-full flex-shrink-0 mt-1.5', {
          'bg-gray-200': is_read,
          'bg-red-600': !is_read,
        })}
      />
      <div className="w-full flex flex-col gap-2">
        <div className="flex w-full justify-between">
          <div className="text-sm font-semibold">
            {fixNameEnum(notification.type)
              .replace('Nav', getRegisterTypeString('nav'))
              .replace('Dividend', getRegisterTypeString('Dividend', { capitalize: true }))
              .replace('Finalized', 'Finalised')}
          </div>
          <div className="text-xs text-neutral-500">{dateToPrint(created_at)}</div>
        </div>
        <span className="text-xs line-clamp-2">{text}</span>
      </div>
    </div>
  );
};

export default IssuerDashboardNotificationItem;
